import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import { getMyStudentProjects, getMyStudentAttendance, getMyStudentAssignments, getMyStudentClasses } from '../../services/studentService';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import styles from './StudentDashboard.module.css';

const StudentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsData, attendanceData, assignmentsData, classesData] = await Promise.all([
        getMyStudentProjects(),
        getMyStudentAttendance(),
        getMyStudentAssignments(),
        getMyStudentClasses()
      ]);
      
      setProjects(projectsData);
      setAttendance(attendanceData);
      setAssignments(assignmentsData);
      
      // Filter upcoming classes
      const now = new Date();
      const upcoming = classesData.filter(cls => new Date(cls.class_date) >= now).slice(0, 5);
      setUpcomingClasses(upcoming);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  const activeProjects = projects.filter(p => p.status === 'in_progress').length;
  
  // Count pending assignments (assigned, overdue, or returned - anything not completed)
  const pendingAssignments = assignments.filter(a => {
    const status = a.submission_status;
    return !status || status === 'assigned' || status === 'overdue' || status === 'returned';
  }).length;
  
  // Prepare attendance pie chart data
  const attendanceChartData = [
    { name: 'Attended', value: attendance.attended || 0, color: '#4CAF50' },
    { name: 'Missed', value: attendance.missed || 0, color: '#F44336' }
  ];

  return (
    <div className={styles.dashboard}>
      <h1 className={styles.pageTitle}>Student Dashboard</h1>

      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="projects" size={32} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{activeProjects}</h3>
            <p className={styles.statLabel}>Active Projects</p>
            <span className={styles.statSubtext}>out of {projects.length} total</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="classes" size={32} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{upcomingClasses.length}</h3>
            <p className={styles.statLabel}>Upcoming Classes</p>
            <span className={styles.statSubtext}>next scheduled</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="assignments" size={32} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{pendingAssignments}</h3>
            <p className={styles.statLabel}>Pending Assignments</p>
            <span className={styles.statSubtext}>to submit</span>
          </div>
        </Card>

        <Card className={styles.statCard}>
          <div className={styles.statIcon}><AppIcon name="attendance" size={32} /></div>
          <div className={styles.statContent}>
            <h3 className={styles.statValue}>{Math.round(attendance.attendance_percentage || 0)}%</h3>
            <p className={styles.statLabel}>Attendance Rate</p>
            <span className={styles.statSubtext}>{attendance.attended || 0} / {attendance.total_classes || 0} classes</span>
          </div>
        </Card>
      </div>

      <div className={styles.chartsSection}>
        <Card title="Attendance Overview">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={attendanceChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {attendanceChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Upcoming Classes">
          <div className={styles.classesList}>
            {upcomingClasses.length === 0 ? (
              <p className={styles.empty}>No upcoming classes</p>
            ) : (
              upcomingClasses.map(cls => (
                <div key={cls.id} className={styles.classItem}>
                  <div className={styles.classInfo}>
                    <h4>Class {cls.class_number}</h4>
                    <p className={styles.classDate}>
                      {new Date(cls.class_date).toLocaleDateString()} at {cls.class_time}
                    </p>
                  </div>
                  {cls.meet_link && (
                    <a href={cls.meet_link} target="_blank" rel="noopener noreferrer" className={styles.joinBtn}>
                      Join
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default StudentDashboard;

